/**
 * The mark a name chip wears when its person is out (red) or asking to be
 * (amber): a left stripe on the chip, a small badge after the name, and the
 * same tooltip text everywhere. One component so Day, Line, Week and Week ·
 * by line read identically.
 */
import React from "react";

import { AvailabilityRow } from "../../../../../services/roster-assignments-api";
import { availabilityBadge,AvailabilityTone } from "../utils/availability"; // eslint-disable-line sort-imports
import { lbb } from "../utils/tokens";

export const toneColor = (tone: AvailabilityTone): string => (tone === "danger" ? lbb.brick : lbb.warning);

/** Width of the stripe; set once so every chip lines up. */
const STRIPE_PX = 3;

/** The left stripe for a chip whose person is out / requesting; nothing when they are expected. */
export const availabilityStripe = (row: AvailabilityRow | null | undefined): React.CSSProperties =>
	row ? { borderLeft: `${STRIPE_PX}px solid ${toneColor(availabilityBadge(row).tone)}` } : {};

/** The tooltip fragment — " · Time Off · approved · Tue 6 Oct 14:30–23:00" — or "" when expected. */
export const availabilityTitle = (row: AvailabilityRow | null | undefined): string =>
	row ? ` · ${availabilityBadge(row).tooltip}` : "";

/** Dimmed, never struck through: the name must stay legible for the supervisor replacing it. */
export const OUT_NAME_OPACITY = 0.62;

export const AvailabilityBadge: React.FC<{
	row: AvailabilityRow;
	/** Font size of the badge text; chips use 9, rows 10. */
	size?: number;
	/** On a selected (brick) card the badge inverts so it still reads. */
	inverted?: boolean;
}> = ({ row, size = 9, inverted = false }) => {
	const badge = availabilityBadge(row);
	const color = toneColor(badge.tone);
	return (
		<span
			role="img"
			aria-label={badge.tooltip}
			title={badge.tooltip}
			style={{
				display: "inline-block",
				padding: "0 4px",
				borderRadius: 2,
				background: inverted ? lbb.paper : color,
				color: inverted ? color : lbb.paper,
				fontFamily: lbb.fontUi,
				fontSize: size,
				fontWeight: 800,
				letterSpacing: "0.08em",
				lineHeight: `${size + 5}px`,
				whiteSpace: "nowrap",
				flexShrink: 0,
			}}
		>
			{badge.text}
		</span>
	);
};

/** "· 1 out" after a crew count, in the danger colour; nothing when nobody is out. */
export const OutCount: React.FC<{ count: number; size?: number }> = ({ count, size = 11 }) =>
	count > 0 ? (
		<span
			style={{ fontFamily: lbb.fontMono, fontSize: size, fontWeight: 700, color: lbb.brick, whiteSpace: "nowrap" }}
			title={`${count} assigned ${count === 1 ? "person is" : "people are"} out — replace from the card's menu`}
		>
			{" "}
			· {count} out
		</span>
	) : null;
